import Badge, { type BadgeTone } from '@/components/base/Badge';
import { formatDateTime, formatMoney } from '@/utils/format';
import type { ReconStatus, Transaction } from '@/types/portal';
import { originMeta } from './originMeta';

interface TransactionTableProps {
  rows: Transaction[];
  startIndex: number;
  onSelect: (row: Transaction) => void;
  onOpenSource: (row: Transaction) => void;
}

const reconMeta: Record<ReconStatus, { tone: BadgeTone; icon: string }> = {
  Reconciled: { tone: 'accent', icon: 'ri-check-double-line' },
  Pending: { tone: 'secondary', icon: 'ri-time-line' },
  Unmatched: { tone: 'primary', icon: 'ri-error-warning-line' },
  Exception: { tone: 'primary', icon: 'ri-alert-line' },
};

export default function TransactionTable({
  rows,
  startIndex,
  onSelect,
  onOpenSource,
}: TransactionTableProps) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
          <i className="ri-exchange-dollar-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
          No transactions match
        </h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Try another wallet, type or date range, or clear the search.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className="row-accent w-full min-w-[1180px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            <th className="w-12 px-4 py-3 font-semibold">#</th>
            <th className="px-3 py-3 font-semibold">Date</th>
            <th className="px-3 py-3 font-semibold">Type</th>
            <th className="px-3 py-3 font-semibold">Recipient</th>
            <th className="px-3 py-3 font-semibold">Description</th>
            <th className="px-3 py-3 text-right font-semibold">Amount</th>
            <th className="px-3 py-3 font-semibold">Ref</th>
            <th className="px-3 py-3 font-semibold">Reconciled</th>
            <th className="px-4 py-3 text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const isCredit = row.direction === 'Credit';
            const origin = originMeta[row.origin];
            const recon = reconMeta[row.recon];
            return (
              <tr
                key={row.id}
                className="border-b border-background-100 last:border-0 transition-colors hover:bg-background-100"
              >
                <td className="px-4 py-3.5 text-xs text-foreground-400">
                  {startIndex + index}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-xs text-foreground-700">
                  {formatDateTime(row.createdAt)}
                </td>
                <td className="px-3 py-3.5">
                  <Badge tone={origin.tone} icon={origin.icon}>
                    {origin.label}
                  </Badge>
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-sm text-foreground-900">{row.party}</p>
                  <p className="text-xs text-foreground-500">{row.partyType}</p>
                </td>
                <td className="px-3 py-3.5">
                  <p className="max-w-[260px] truncate text-sm text-foreground-700" title={row.description}>
                    {row.description}
                  </p>
                  <p className="text-xs text-foreground-400">{row.rail}</p>
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-right">
                  <p
                    className={`text-sm font-medium ${
                      isCredit ? 'text-accent-700' : 'text-foreground-950'
                    }`}
                  >
                    {isCredit ? '+' : '−'}
                    {formatMoney(row.amount, row.currency)}
                  </p>
                  <p className="text-xs text-foreground-400">{row.wallet}</p>
                </td>
                <td className="px-3 py-3.5">
                  <span
                    className="block max-w-[150px] truncate font-mono text-xs text-primary-700"
                    title={row.reference}
                  >
                    {row.reference}
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  <Badge tone={recon.tone} icon={recon.icon}>
                    {row.recon}
                  </Badge>
                </td>
                <td className="px-4 py-3.5">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => onSelect(row)}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-900"
                      aria-label={`View ${row.reference}`}
                      title="View details"
                    >
                      <i className="ri-eye-line" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onOpenSource(row)}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-900"
                      aria-label={`Open source of ${row.reference}`}
                      title={origin.sourceLabel}
                    >
                      <i className="ri-arrow-right-up-line" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}